/**
 * Sidebar mode store (design directions 5a: "three states, one mechanism").
 *
 * The sidebar is docked (full width, pushes content) or a rail (48px, hover to
 * peek the full menu over the page). The app picks the mode per *view type*:
 * lists/boards/Inbox/Home default to docked, while docs, full task pages and
 * notes default to the rail because they want the width. Toggling with `[`
 * or pinning the peek remembers the choice for that view type.
 *
 * `hidden` is separate and global: the sidebar is gone entirely until the
 * user brings it back.
 */

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type SidebarViewType = 'list' | 'doc' | 'task' | 'note'
export type SidebarMode = 'docked' | 'rail'

export const DEFAULT_SIDEBAR_MODES: Record<SidebarViewType, SidebarMode> = {
  list: 'docked',
  doc: 'rail',
  task: 'rail',
  note: 'rail',
}

type SidebarModeState = {
  modes: Partial<Record<SidebarViewType, SidebarMode>>
  hidden: boolean
  setMode: (viewType: SidebarViewType, mode: SidebarMode) => void
  setHidden: (hidden: boolean) => void
}

export const useSidebarModeStore = create<SidebarModeState>()(
  persist(
    (set) => ({
      modes: {},
      hidden: false,
      setMode: (viewType, mode) =>
        set((state) => ({ modes: { ...state.modes, [viewType]: mode } })),
      setHidden: (hidden) => set({ hidden }),
    }),
    {
      name: 'moly-sidebar-mode',
      storage: createJSONStorage(() => localStorage),
    },
  ),
)

export function resolveSidebarMode(
  modes: Partial<Record<SidebarViewType, SidebarMode>>,
  viewType: SidebarViewType,
): SidebarMode {
  return modes[viewType] ?? DEFAULT_SIDEBAR_MODES[viewType]
}

const TASK_LIST_SEGMENTS = new Set(['today', 'upcoming', 'someday'])

/** Classify a pathname into the view type the sidebar mode is keyed by. */
export function getSidebarViewType(pathname: string): SidebarViewType {
  const segments = pathname.split('/').filter(Boolean)
  // Doc pages: /…/pages/$pageSlug under projects, spaces and space projects.
  const pagesIndex = segments.indexOf('pages')
  if (pagesIndex !== -1 && pagesIndex < segments.length - 1) return 'doc'
  // Notes: /$workspaceSlug/notes/…
  if (segments[1] === 'notes') return 'note'
  // Full task page: /$workspaceSlug/tasks/$taskId (not today/upcoming/someday).
  const [, section, child] = segments
  if (section === 'tasks' && segments.length === 3 && child && !TASK_LIST_SEGMENTS.has(child)) {
    return 'task'
  }
  return 'list'
}
